import { IMAGE_DIMENSIONS } from '@/components/Image/Image'

export const CAPTION_COLORS = {
  default: false,
  error: 'error',
  accent: 'accent',
  webcent: 'webcent'
}

export const CAPTION_POSITION = {
  default: false,
  top: 'top'
}

export const CAPTION_SIZE = {
  default: false,
  big: 'big'
}

export const CAPTION_ICON = {
  Video: 'video',
  Galerie: 'gallery'
}

export const LABEL_COLORS = {
  default: false,
  error: 'error',
  accent: 'accent',
  webcent: 'webcent',
  grey: 'grey',
  greyLight: 'grey-light'
}

export const HEADER_POSITIONS = {
  default: false,
  top: 'top'
}

export const SEPARATOR_POSITIONS = {
  default: false,
  top: 'top',
  bottom: 'bottom',
  left: 'left',
  right: 'right'
}

/**
 * Constants for the possible types, how the article can be positioned over the image
 */
export const ARTICLE_OVERLAYS = {
  bottomfull: 'bottomfull',
  bottomleft: 'bottomleft'
}

/**
 * data class for a teaser element
 * @param elementData the data retrieved from the backend
 * @param options configurable fields in the teaser {{type, width, height, hideImageIcon, articleoverlay, separator, headerIsDivTag, headerPosition}}
 */
export default class Teaser {
  constructor (elementData, options = {}) {
    this.id = elementData.id
    this.type = 'hp-teaser'
    this.isRecommendation = elementData.recommendation || false
    this.articleoverlay = options.articleoverlay || elementData.articleoverlay || false
    this.separator = options.separator || elementData.separator || SEPARATOR_POSITIONS.default
    this.trComponentPath = elementData.trComponentPath

    this.link = elementData.link
      ? {
        href: elementData.link.url || null,
        target: elementData.link.external ? '_blank' : null,
        rel: elementData.link.behaviour || null
      }
      : null

    this.figure = elementData.image?.url
      ? {
        img: {
          url: elementData.image?.url,
          isCoreMedia: elementData.image?.coreMediaImage,
          alt: elementData.image?.altText || null,
          lazy: elementData.image?.lazyLoading ?? true,
          width: options.width ?? IMAGE_DIMENSIONS.teaser.width,
          height: options.height ?? IMAGE_DIMENSIONS.teaser.height
        },
        overImgCaption:
          elementData.overImgCaption?.jackpot1
            ? {
              jackpot1: elementData.overImgCaption?.jackpot1 || '',
              jackpot2: elementData.overImgCaption?.jackpot2 || '',
              chance: elementData.overImgCaption?.chance || '',
              product: elementData.overImgCaption?.product || '',
              position: elementData.overImgCaption?.position ?? CAPTION_POSITION.default,
              size: elementData.overImgCaption?.size ?? CAPTION_SIZE.default,
              color: elementData.overImgCaption?.color ?? CAPTION_COLORS.default
            }
            : null,
        caption:
          elementData.caption?.text || (!options.hideImageIcon && elementData.teaserType)
            ? {
              position: elementData.caption?.position ?? CAPTION_POSITION.default,
              size: elementData.caption?.size ?? CAPTION_SIZE.default,
              color: elementData.caption?.color ?? CAPTION_COLORS.default,
              icon: (!options.hideImageIcon && elementData.teaserType) ? CAPTION_ICON[elementData.teaserType] : null,
              text: elementData.caption?.text || ''
            }
            : null
      }
      : null

    this.article = {
      header: {
        isDivTag: options.headerIsDivTag ?? false,
        position: options.headerPosition ?? HEADER_POSITIONS.default,
        headline: elementData.headline || null,
        label: elementData.viewType
          ? {
            color: elementData.viewType?.color || null,
            highlight: elementData.viewType?.text || null,
            text: elementData.kicker || ''
          }
          : elementData.teaserType
            ? {
              color: LABEL_COLORS.grey,
              highlight: elementData.teaserType,
              text: elementData.kicker || ''
            }
            : elementData.kicker
              ? {
                text: elementData.kicker
              }
              : null
      },
      summary: elementData.text || null,
      footer: this._getFooterData(elementData)
    }
  }

  _getFooterData (footerData) {
    const { authors, modificationDate, comments } = footerData

    if (authors?.length > 1) {
      return [
        'von ' +
        authors
          .map((author) => {
            return this._getShortAuthor(author)
          })
          .join(', ')
      ]
    }

    if (authors?.length && modificationDate) {
      return [`von ${authors[0]}`, this._getTimeAgo(modificationDate)].filter((e) => e)
    }

    if (authors?.length && comments) {
      return [`von ${authors[0]}`, `${comments} Kommentar${comments > 1 ? 'e' : ''}`]
    }

    if (modificationDate && comments) {
      return [this._getTimeAgo(modificationDate), `${comments} Kommentar${comments > 1 ? 'e' : ''}`].filter((e) => e)
    }

    if (authors?.length) {
      return [`von ${authors[0]}`]
    }

    if (modificationDate) {
      return [this._getTimeAgo(modificationDate)].filter((e) => e)
    }

    if (comments) {
      return [`${comments} Kommentar${comments > 1 ? 'e' : ''}`]
    }

    return []
  }

  _getShortAuthor (author) {
    if (!author) {
      return
    }

    const names = author.split(' ')
    if (names.length === 1) {
      return names[0]
    }
    return names[0].slice(0, 1) + '. ' + names[names.length - 1]
  }

  _getTimeAgo (timestamp) {
    const structure = {
      year: 31536000,
      month: 2592000,
      week: 604800, // uncomment row to ignore
      day: 86400, // feel free to add your own row
      hour: 3600,
      minute: 60,
      second: 1
    }

    let delta = Math.abs(timestamp - new Date().getTime() / 1000)
    const res = {}

    for (const key in structure) {
      res[key] = Math.floor(delta / structure[key])
      delta -= res[key] * structure[key]
    }

    if (res.hour >= 5 || res.day > 0 || res.week > 0 || res.month > 0 || res.year > 0) {
      return null
    }

    if (res.hour < 1) {
      if (res.minute < 1) {
        return 'gerade eben'
      }
      return `vor ${res.minute} Minute${res.minute > 1 ? 'n' : ''}`
    }
    return `vor ${res.hour} Stunde${res.hour > 1 ? 'n' : ''}`
  }
}
